import React, {useState} from "react";
import {shallowEqual, useSelector} from "react-redux";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import SearchIcon from "@mui/icons-material/Search";
import DescriptionIcon from "@mui/icons-material/Description";
import {axiosRun} from "../utils/axiosWrap";
import {config} from "../config";

const labels = {
    FirstName: "Ім'я",
    LastName: "Прізвище",
    MiddleName: "По батькові",
    FirstNameEn: "Ім'я латиницею",
    LastNameEn: "Прізвище латиницею",
    MiddleNameEn: "По батькові латиницею",
    Birthday: "Дата народження",
    Id_PersonSex: "ID статі",
    SexId: "Стать",
    Id_Country: "ID країни",
    CountryId: "Країна",
    IPNNumber: "ІПН",
    Unzr: "УНЗР",
    PersonCode: "PersonCode",
    Id_PersonDocument: "ID документа",
    Id_PersonDocumentType: "Тип документа",
    idPersonDocument: "ID документа персони",
    idPersonDocumentType: "ID типу документа персони",
    PersonDocumentTypeName: "Тип документа",
    personDocumentTypeName: "Тип документа",
    DocumentSeries: "Серія документа",
    DocumentNumbers: "Номер документа",
    DocumentDateGet: "Дата видачі",
    DocumentIssued: "Ким виданий",
    DocumentExpiredDate: "Дійсний до",
    PersonDocumentAwardTypeId: "ID типу відзнаки",
    PersonDocumentsAwardTypeName: "Відзнака",
    IsCancelled: "Анульований",
    IsDeleted: "Не належить особі",
    Description: "Примітки",
    CancelReason: "Причина анулювання",
    DeletedReason: "Причина позначки",
    IsForeignEntrantDocument: "Документ іноземного зразка",
    Email: "Email",
    ContactPhone: "Телефон",
    HomePhone: "Домашній телефон"
};

const fieldAliases = {
    FirstName: "firstName",
    LastName: "lastName",
    MiddleName: "middleName",
    FirstNameEn: "firstNameEn",
    LastNameEn: "lastNameEn",
    MiddleNameEn: "middleNameEn",
    Birthday: "birthday",
    Id_PersonSex: "id_PersonSex",
    SexId: "sexId",
    Id_Country: "id_Country",
    CountryId: "countryId",
    IPNNumber: "ipnNumber",
    Unzr: "unzr",
    PersonCode: ["personCode", "PersonCodeU", "personCodeU"],
    Id_PersonDocument: ["id_PersonDocument", "idPersonDocument"],
    Id_PersonDocumentType: ["id_PersonDocumentType", "idPersonDocumentType"],
    DocumentSeries: "documentSeries",
    DocumentNumbers: "documentNumbers",
    DocumentDateGet: "documentDateGet",
    DocumentIssued: "documentIssued",
    DocumentExpiredDate: "documentExpiredDate",
    IsCancelled: "isCancelled",
    IsDeleted: "isDeleted",
    IsForeignEntrantDocument: "isForeignEntrantDocument",
    Email: "email",
    ContactPhone: "contactPhone",
    HomePhone: "homePhone"
};

const mainFields = [
    "LastName",
    "FirstName",
    "MiddleName",
    "LastNameEn",
    "FirstNameEn",
    "MiddleNameEn",
    "Birthday",
    "SexId",
    "CountryId",
    "IPNNumber",
    "Unzr",
    "PersonCode",
    "Email",
    "ContactPhone",
    "HomePhone"
];

const documentFields = [
    "Id_PersonDocument",
    "Id_PersonDocumentType",
    "DocumentSeries",
    "DocumentNumbers",
    "DocumentDateGet",
    "DocumentIssued",
    "DocumentExpiredDate"
];

const availableDocumentFields = [
    "idPersonDocument",
    "idPersonDocumentType",
    "DocumentSeries",
    "DocumentNumbers",
    "DocumentDateGet",
    "DocumentIssued",
    "PersonDocumentsAwardTypeName",
    "IsCancelled",
    "IsForeignEntrantDocument",
    "Description",
    "CancelReason",
    "DeletedReason"
];

const formatDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat("uk-UA").format(date);
};

const formatValue = (field, value) => {
    if (value === null || value === undefined || value === "") {
        return "немає даних";
    }

    if (typeof value === "boolean") {
        return value ? "Так" : "Ні";
    }

    if (field.toLowerCase().includes("date") || field === "Birthday") {
        return formatDate(value);
    }

    return value;
};

const getFieldValue = (data, field) => {
    if (!data) {
        return undefined;
    }

    if (field === "Id_PersonDocumentType") {
        return data.DocumentTypeName
            || data.documentTypeName
            || data.PersonDocumentTypeName
            || data.personDocumentTypeName
            || data[field]
            || data.id_PersonDocumentType
            || data.idPersonDocumentType;
    }

    if (data[field] !== undefined) {
        return data[field];
    }

    const aliases = Array.isArray(fieldAliases[field]) ? fieldAliases[field] : [fieldAliases[field]];
    const foundAlias = aliases.find((alias) => alias && data[alias] !== undefined);

    return foundAlias ? data[foundAlias] : undefined;
};

const isPositiveValue = (value) => {
    return value === true || value === 1 || value === "1" || String(value).toLowerCase() === "true";
};

const normalizeDocumentsList = (response) => {
    if (Array.isArray(response)) {
        return response;
    }

    if (Array.isArray(response?.data)) {
        return response.data;
    }

    if (Array.isArray(response?.documents)) {
        return response.documents;
    }

    return [];
};

const getErrorText = (error) => {
    const message = error?.data?.message || error?.data?.details || "Не вдалося отримати дані з ЄДЕБО";

    if (typeof message === "string") {
        return message;
    }

    return JSON.stringify(message);
};

const FieldList = ({title, fields, data}) => (
    <Box sx={{mt: 3}}>
        <Typography variant="h6" sx={{fontWeight: 700, mb: 1.5}}>
            {title}
        </Typography>
        <Grid container spacing={2}>
            {fields.map((field) => (
                <Grid item xs={12} sm={6} md={4} key={field}>
                    <Box
                        sx={{
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            p: 1.5,
                            minHeight: 76,
                            bgcolor: "#fafafa"
                        }}
                    >
                        <Typography variant="caption" sx={{color: "#616161", display: "block", mb: 0.5}}>
                            {labels[field] || field}
                        </Typography>
                        <Typography variant="body1" sx={{fontWeight: 600, overflowWrap: "anywhere"}}>
                            {formatValue(field, getFieldValue(data, field))}
                        </Typography>
                    </Box>
                </Grid>
            ))}
        </Grid>
    </Box>
);

const DocumentCard = ({document, index}) => {
    const documentType = getFieldValue(document, "PersonDocumentTypeName")
        || getFieldValue(document, "Id_PersonDocumentType")
        || `Документ ${index + 1}`;
    const series = getFieldValue(document, "DocumentSeries");
    const number = getFieldValue(document, "DocumentNumbers");
    const documentNumber = [series, number].filter(Boolean).join(" ");
    const isCancelled = isPositiveValue(getFieldValue(document, "IsCancelled"));
    const isDeleted = isPositiveValue(getFieldValue(document, "IsDeleted"));

    return (
        <Box
            sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                p: 2,
                bgcolor: "#fff"
            }}
        >
            <Stack
                direction={{xs: "column", sm: "row"}}
                spacing={1.5}
                alignItems={{xs: "flex-start", sm: "center"}}
                justifyContent="space-between"
                sx={{mb: 1.5}}
            >
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{minWidth: 0}}>
                    <DescriptionIcon color="primary" />
                    <Box sx={{minWidth: 0}}>
                        <Typography variant="subtitle1" sx={{fontWeight: 700, overflowWrap: "anywhere"}}>
                            {formatValue("PersonDocumentTypeName", documentType)}
                        </Typography>
                        <Typography variant="body2" sx={{color: "#616161", overflowWrap: "anywhere"}}>
                            {documentNumber || "Номер документа не вказано"}
                        </Typography>
                    </Box>
                </Stack>
                <Stack direction="row" spacing={1} sx={{flexWrap: "wrap", rowGap: 1}}>
                    <Chip
                        size="small"
                        color={isCancelled || isDeleted ? "warning" : "success"}
                        label={isCancelled || isDeleted ? "Є обмеження" : "Активний"}
                    />
                    {isCancelled && <Chip size="small" color="error" variant="outlined" label="Анульований" />}
                    {isDeleted && <Chip size="small" color="error" variant="outlined" label="Не належить особі" />}
                </Stack>
            </Stack>

            <Grid container spacing={1.5}>
                {availableDocumentFields.map((field) => (
                    <Grid item xs={12} sm={6} md={4} key={field}>
                        <Box sx={{bgcolor: "#fafafa", borderRadius: "8px", p: 1.25, minHeight: 66}}>
                            <Typography variant="caption" sx={{color: "#616161", display: "block", mb: 0.5}}>
                                {labels[field] || field}
                            </Typography>
                            <Typography variant="body2" sx={{fontWeight: 600, overflowWrap: "anywhere"}}>
                                {formatValue(field, getFieldValue(document, field))}
                            </Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

const DocumentsList = ({documents, errorMessage}) => (
    <Box sx={{mt: 3}}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{mb: 1.5, flexWrap: "wrap", rowGap: 1}}>
            <Typography variant="h6" sx={{fontWeight: 700}}>
                Доступні документи
            </Typography>
            <Chip size="small" label={`${documents.length} шт.`} variant="outlined" />
        </Stack>

        {errorMessage && (
            <Alert severity="warning" sx={{mb: 2}}>
                {errorMessage}
            </Alert>
        )}

        {!errorMessage && documents.length === 0 && (
            <Alert severity="info">
                Документи за цим PersonCode не знайдені.
            </Alert>
        )}

        {documents.length > 0 && (
            <Box
                sx={{
                    maxHeight: 520,
                    overflowY: "auto",
                    pr: 1,
                    display: "grid",
                    gap: 1.5
                }}
            >
                {documents.map((document, index) => (
                    <DocumentCard
                        key={`${getFieldValue(document, "idPersonDocument") || getFieldValue(document, "DocumentNumbers") || index}`}
                        document={document}
                        index={index}
                    />
                ))}
            </Box>
        )}
    </Box>
);

const EdeboPersonInfo = ({userId}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [personInfo, setPersonInfo] = useState(null);
    const [personDocuments, setPersonDocuments] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [documentsErrorMessage, setDocumentsErrorMessage] = useState("");
    const roles = useSelector((state) => state.users.user, shallowEqual).user.roles;
    const isGroupLead = roles === "ADMIN_GROUP_LEAD";

    if (isGroupLead) {
        return (
            <Box sx={{width: "100%"}}>
                <Alert severity="warning">
                    У вас недостатньо прав
                </Alert>
            </Box>
        );
    }

    const loadPersonInfo = async () => {
        setIsLoading(true);
        setErrorMessage("");
        setDocumentsErrorMessage("");
        setPersonDocuments([]);

        try {
            const link = config.apiLinks.getEdeboPersonInfo.replace("%id%", userId);
            const response = await axiosRun(link, "GET");
            setPersonInfo(response);

            const personCode = getFieldValue(response, "PersonCode");

            if (!personCode) {
                setDocumentsErrorMessage("У відповіді ЄДЕБО не знайдено PersonCode для завантаження документів.");
                return;
            }

            try {
                const documentsLinkTemplate = config.apiLinks.getEdeboPersonDocuments;
                const documentsLink = documentsLinkTemplate
                    .replace("%id%", userId)
                    .replace("%personCode%", encodeURIComponent(personCode));
                const documentsResponse = await axiosRun(documentsLink, "GET");

                setPersonDocuments(normalizeDocumentsList(documentsResponse));
            } catch (documentsError) {
                setDocumentsErrorMessage(getErrorText(documentsError));
            }
        } catch (error) {
            setPersonInfo(null);
            setPersonDocuments([]);
            setErrorMessage(getErrorText(error));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{width: "100%"}}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 2, flexWrap: "wrap", rowGap: 1}}>
                <Button
                    variant="contained"
                    startIcon={isLoading ? <CircularProgress color="inherit" size={18} /> : <SearchIcon />}
                    onClick={loadPersonInfo}
                    disabled={isLoading}
                >
                    Отримати ДОДАТКОВІ ДАНІ
                </Button>
                {personInfo && getFieldValue(personInfo, "PersonCode") && (
                    <Chip label={`PersonCode: ${getFieldValue(personInfo, "PersonCode")}`} variant="outlined" />
                )}
            </Stack>

            {errorMessage && (
                <Alert severity="error" sx={{mb: 2}}>
                    {errorMessage}
                </Alert>
            )}

            {personInfo && (
                <Paper elevation={0} sx={{border: "1px solid #e0e0e0", borderRadius: "8px", p: 2.5}}>
                    <Typography variant="h5" sx={{fontWeight: 700}}>
                        {[
                            getFieldValue(personInfo, "LastName"),
                            getFieldValue(personInfo, "FirstName"),
                            getFieldValue(personInfo, "MiddleName")
                        ].filter(Boolean).join(" ") || "Дані з ЄДЕБО"}
                    </Typography>
                    <Typography variant="body2" sx={{color: "#616161", mt: 0.5}}>
                        Відповідь /api/physPersons/info
                    </Typography>

                    <FieldList title="Основні дані" fields={mainFields} data={personInfo} />
                    <Divider sx={{my: 3}} />
                    <FieldList title="Документ" fields={documentFields} data={personInfo} />
                    <Divider sx={{my: 3}} />
                    <DocumentsList documents={personDocuments} errorMessage={documentsErrorMessage} />
                </Paper>
            )}
        </Box>
    );
};

export default EdeboPersonInfo;
